import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { waitForImages } from '@/lib/waitForImages';
import { DeliveryReportDocument, type DeliveryReportBranding, type DeliveryReportRow } from '@/components/partners/DeliveryReportDocument';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

const SIGNED_URL_TTL = 60 * 60;

async function pathToSignedUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { data } = await supabase.storage.from('invoice-assets').createSignedUrl(path, SIGNED_URL_TTL);
  return data?.signedUrl ?? null;
}

interface OrderItemRow {
  order_id: string;
  qty: number;
}

interface ArLedgerRow {
  amount_bdt: number;
  amount_paid_bdt: number;
}

interface OrderRow {
  id: string;
  order_no: string;
  total_bdt: number;
  placed_at: string;
  partner_id: string | null;
  is_gift: boolean;
  core_ar_ledger: ArLedgerRow | ArLedgerRow[] | null;
  core_order_items: OrderItemRow[] | null;
}

function paymentStatusOf(o: OrderRow): 'gift' | 'paid' | 'unpaid' {
  if (o.is_gift) return 'gift';
  const ar = Array.isArray(o.core_ar_ledger) ? o.core_ar_ledger[0] : o.core_ar_ledger;
  if (!ar) return 'paid';
  return ar.amount_paid_bdt >= ar.amount_bdt ? 'paid' : 'unpaid';
}

// Mirrors ERP's version of this same page (CorePartnerOrdersPrint), and the
// three filters on this app's own Orders tab, so "Print" always reports
// exactly the list the user was just looking at.
export default function AdminResellerOrdersPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get('autoprint') === '1';
  const typeFilter = (searchParams.get('type') as 'all' | 'sale' | 'gift') || 'all';
  const paymentFilter = (searchParams.get('payment') as 'all' | 'paid' | 'unpaid') || 'all';
  const monthFilter = searchParams.get('month') || '';

  const [branding, setBranding] = useState<DeliveryReportBranding | null>(null);
  const [rows, setRows] = useState<DeliveryReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [generatedAt] = useState(() => new Date());
  const docRef = useRef<HTMLDivElement>(null);
  const autoPrinted = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: tpl }, { data: partnerRows }, { data: orderRows }] = await Promise.all([
      supabase.from('invoice_templates').select('issuer_name, issuer_address, issuer_contact, issuer_email, logo_url').eq('doc_type', 'invoice').maybeSingle(),
      supabase.from('core_partners').select('id, name'),
      supabase
        .from('core_orders')
        .select('id, order_no, total_bdt, placed_at, partner_id, is_gift, core_ar_ledger(amount_bdt, amount_paid_bdt), core_order_items(order_id, qty)')
        .eq('channel', 'pharmacy_b2b')
        .eq('status', 'delivered')
        .order('placed_at', { ascending: false }),
    ]);
    const logoUrl = await pathToSignedUrl(tpl?.logo_url ?? null);
    setBranding({
      issuer_name: tpl?.issuer_name ?? null,
      issuer_address: tpl?.issuer_address ?? null,
      issuer_contact: tpl?.issuer_contact ?? null,
      issuer_email: tpl?.issuer_email ?? null,
      logo_url: logoUrl,
    });

    const partnerName = new Map((partnerRows ?? []).map((p) => [p.id as string, p.name as string]));
    const filtered = ((orderRows as OrderRow[]) ?? []).filter((o) => {
      if (typeFilter === 'sale' && o.is_gift) return false;
      if (typeFilter === 'gift' && !o.is_gift) return false;
      if (paymentFilter !== 'all' && paymentStatusOf(o) !== paymentFilter) return false;
      if (monthFilter && !o.placed_at.startsWith(monthFilter)) return false;
      return true;
    });
    setRows(
      filtered.map((o) => ({
        order_no: o.order_no,
        partner_name: partnerName.get(o.partner_id ?? '') ?? '—',
        order_date: o.placed_at,
        pcs: (o.core_order_items ?? []).reduce((sum, it) => sum + it.qty, 0),
        amount_bdt: o.total_bdt,
      })),
    );
    setLoading(false);
  }, [typeFilter, paymentFilter, monthFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePrint = useCallback(async () => {
    setPrinting(true);
    if (docRef.current) await waitForImages(docRef.current);
    window.print();
    setPrinting(false);
  }, []);

  useEffect(() => {
    if (autoPrint && !loading && branding && !autoPrinted.current) {
      autoPrinted.current = true;
      handlePrint();
    }
  }, [autoPrint, loading, branding, handlePrint]);

  const filterLabel = [
    'Delivered orders',
    typeFilter !== 'all' ? (typeFilter === 'gift' ? 'Gift only' : 'Sale only') : null,
    paymentFilter !== 'all' ? (paymentFilter === 'paid' ? 'Paid only' : 'Unpaid only') : null,
    monthFilter ? new Date(`${monthFilter}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      {/* Same reasoning as ERP's version — a delivery list can span many
          rows/pages, unlike a single-page invoice. */}
      <style>{'@media print { @page { size: A4; margin: 15mm; } }'}</style>
      <div className="print:hidden flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/resellers')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back to resellers">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-[20px] font-bold text-ink">Delivery Report</h1>
        </div>
        <Button onClick={handlePrint} loading={printing} disabled={loading}>
          <Printer className="w-4 h-4" /> Print
        </Button>
      </div>

      {loading || !branding ? (
        <Spinner />
      ) : (
        <div ref={docRef} className="shadow-modal print:shadow-none">
          <DeliveryReportDocument branding={branding} rows={rows} generatedAt={generatedAt} filterLabel={filterLabel} />
        </div>
      )}
    </AppShell>
  );
}
